import { transition, trigger, useAnimation } from '@angular/animations';
import { Component } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Params, Router } from '@angular/router';
import { AccountType } from '@mnz-core/models/_enums';
import { IRootState } from '@mnz-core/modules/store/models/root-state';
import * as routerSelectors from '@mnz-core/modules/store/selectors/router.selectors';
import * as accountActions from '@mnz-modules/login/store/actions/account.actions';
import * as accountSelectors from '@mnz-modules/login/store/selectors/account.selectors';
import { formUtils } from '@mnz-shared/shared.constants';
import { select, Store } from '@ngrx/store';
import { fadeIn } from 'ng-animate';
import { first, map, tap } from 'rxjs/operators';
import * as authSelectors from '@mnz-modules/login/store/selectors/auth.selectors';
import { TranslateService } from '@ngx-translate/core';
import { createEmailForm } from '@mnz-modules/login/login.constants';
import { Observable, of } from 'rxjs';

@Component({
  selector: 'mnz-lost-credentials',
  templateUrl: 'lost-credentials.page.html',
  animations: [trigger('fadeIn', [transition('* => *', useAnimation(fadeIn, { params: { timing: 0.5 } }))])],
})
export class LostCredentialsPage {
  emailErrorMessage: Observable<string>;

  constructor(
    private readonly _store$: Store<IRootState>,
    private readonly _router: Router,
    private readonly _translate: TranslateService,
    private readonly _formBuilder: FormBuilder
  ) {
    this._store$
      .pipe(
        select(authSelectors.getInput),
        first(),
        tap((email) => {
          email && this.emailForm$.setValue({ email });
        })
      )
      .subscribe();
  }

  readonly emailForm$ = createEmailForm(this._formBuilder);
  readonly loading$ = this._store$.pipe(select(accountSelectors.getLoading));

  readonly accountType$ = this._store$.pipe(
    select(routerSelectors.getParams),
    map<Params, AccountType>(({ account }) => account)
  );

  async resetAccount(form): Promise<void> {
    if (form.status === 'INVALID') {
      if (form.controls.email.hasError('required')) {
        this.checkEmail(form);
        document.getElementById('email').shadowRoot.querySelector('input').focus();
      }
      return formUtils.touchControls({ controls: form.controls.email });
    }

    const accountType = await this.accountType$.pipe(first()).toPromise();
    this._store$.dispatch(
      accountActions.resetAccount({
        accountType,
        email: String(form.controls.email.value).toLowerCase(),
      })
    );
  }

  public onKeyDown(form, event) {
    if (event.detail.event.key === 'Enter') {
      this.resetAccount(form);
    }
  }

  async backToLogin(): Promise<void> {
    const accountType = await this.accountType$.pipe(first()).toPromise();

    const queryParams =
      accountType === AccountType.CARD_HOLDER
        ? {
            email: await this._store$
              .pipe(
                select(routerSelectors.getQueryParams),
                map<Params, string>(({ email }) => email),
                first()
              )
              .toPromise(),
          }
        : Object();

    this._router.navigate([`/inloggen/${accountType}`], { queryParams });
  }

  public checkEmail(form) {
    const email = form.controls.email;
    const isTouchedDirtyAndInvalid = !email.valid && email.touched && email.dirty;

    if (isTouchedDirtyAndInvalid) {
      if (email.hasError('email') && !email.hasError('required')) {
        this.emailErrorMessage = this._translate.get(`LOGIN.INPUT_ERROR.INVALID_EMAIL`);
      } else if (email.hasError('required')) {
        this.emailErrorMessage = this._translate.get(`LOGIN.INPUT_ERROR.REQUIRED_EMAIL`);
      } else if (email.hasError('maxlength') && !email.hasError('email')) {
        this.emailErrorMessage = this._translate.get(`LOGIN.INPUT_ERROR.LENGTH_EMAIL`);
      }
    } else {
      this.emailErrorMessage = of('');
    }
  }
}
